import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Search, X, FileText, User, Loader2 } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const TYPE_LABEL = { devis: "Devis", facture: "Facture", bon_commande: "Bon de commande", bon_livraison: "Bon de livraison" };

export default function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ clients: [], documents: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setRes({ clients: [], documents: [] });
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setRes({ clients: [], documents: [] }); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setRes(data);
      } catch (_) {}
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (!open) return null;
  const go = (path) => { onClose(); navigate(path); };
  const empty = q.trim() && !loading && res.clients.length === 0 && res.documents.length === 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-[10vh] px-4" onClick={onClose} data-testid="search-modal">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
            placeholder="Rechercher un client, un devis, une facture…"
            className="flex-1 py-4 text-sm focus:outline-none" />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto scrollbar-thin">
          {!q.trim() && <p className="p-6 text-center text-sm text-slate-400">Tapez le nom d'un client ou un numéro de document…</p>}
          {empty && <p className="p-6 text-center text-sm text-slate-400" data-testid="search-empty">Aucun résultat pour « {q} »</p>}
          {res.clients.length > 0 && (
            <div className="px-2 py-2">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Clients</div>
              {res.clients.map((c) => (
                <button key={c.id} onClick={() => go("/documents")} data-testid={`search-client-${c.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-sky-600" /></div>
                  <div className="min-w-0"><div className="text-sm font-medium text-navy truncate">{c.name}</div><div className="text-xs text-slate-500 truncate">{c.city || c.email || "Client"}</div></div>
                </button>
              ))}
            </div>
          )}
          {res.documents.length > 0 && (
            <div className="px-2 py-2 border-t border-slate-50">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Documents</div>
              {res.documents.map((d) => (
                <button key={d.id} onClick={() => go(`/documents/${d.id}`)} data-testid={`search-doc-${d.id}`}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-slate-500" /></div>
                    <div className="min-w-0"><div className="text-sm font-medium text-navy truncate">{d.title || d.number}</div><div className="text-xs text-slate-500 font-mono truncate">{TYPE_LABEL[d.type]} · {d.number} · {d.client_name}</div></div>
                  </div>
                  <span className="text-sm font-semibold text-navy shrink-0 tabular">{euro(d.total_ttc)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
